import { Badge, Text } from "@/components/ui";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import type { ReactNode } from "react";
import type { ProjectDraft } from "./phase-project-editor-utils";

export function PhaseProjectSummary({
  draft,
  incidentTypes,
  action,
}: {
  draft: ProjectDraft;
  incidentTypes: { id: string; label: string }[];
  action?: ReactNode;
}) {
  const assignedIncidentTypes = draft.incidentTypeIds.map(
    (id) =>
      incidentTypes.find((type) => type.id === id)?.label ?? "Unknown type",
  );
  const actionCount = draft.phases.reduce(
    (total, phase) => total + phase.actionDefinitionIds.length,
    0,
  );
  const widgetCount = draft.phases.reduce(
    (total, phase) => total + phase.widgets.length,
    0,
  );

  return (
    <div className="border-b p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <Text variant="title">{draft.title}</Text>
            {assignedIncidentTypes.length > 0 ? (
              <div
                className="flex flex-wrap items-center gap-1.5"
                aria-label="Incident types"
              >
                {assignedIncidentTypes.slice(0, 3).map((label, index) => (
                  <Badge
                    key={draft.incidentTypeIds[index]}
                    variant="outline"
                    className="text-muted-foreground max-w-40 truncate font-normal"
                    title={label}
                  >
                    {label}
                  </Badge>
                ))}
                {assignedIncidentTypes.length > 3 ? (
                  <TooltipWrapper
                    message={
                      <ul className="max-h-56 space-y-1 overflow-y-auto text-sm leading-5">
                        {assignedIncidentTypes.slice(3).map((label, index) => (
                          <li key={draft.incidentTypeIds[index + 3]}>
                            {label}
                          </li>
                        ))}
                      </ul>
                    }
                  >
                    <span
                      className="inline-flex cursor-help"
                      tabIndex={0}
                      aria-label={`${assignedIncidentTypes.length - 3} more incident types`}
                    >
                      <Badge
                        variant="outline"
                        className="text-muted-foreground font-normal"
                      >
                        +{assignedIncidentTypes.length - 3} more
                      </Badge>
                    </span>
                  </TooltipWrapper>
                ) : null}
              </div>
            ) : null}
          </div>
          {draft.description ? (
            <Text variant="subHeading">{draft.description}</Text>
          ) : null}
          <p className="text-muted-foreground text-sm">
            {draft.phases.length}{" "}
            {draft.phases.length === 1 ? "phase" : "phases"}
            {" · "}
            {actionCount} {actionCount === 1 ? "action" : "actions"}
            {" · "}
            {widgetCount} {widgetCount === 1 ? "widget" : "widgets"}
          </p>
        </div>
        {action}
      </div>
    </div>
  );
}
